import type { CustomOption, CustomOptionValue } from "core/catalog";
import { useEffect, useMemo, useRef } from "react";
import { LabelInput } from "~/design-system/components";
import { CheckboxInput } from "~/design-system/components/form/CheckboxInput";
import { CustomOptionLabel } from "./CustomOptionLabel";
import { OptionValueRow } from "./OptionValueRow";

type CheckboxInputOptionProps = {
  customOption: CustomOption;
  onChange?: (value: string[] | boolean) => void;
};

export function CheckboxInputOption({
  customOption,
  onChange,
}: CheckboxInputOptionProps) {
  const values = useMemo(
    () => customOption.values || [],
    [customOption.values],
  );
  const selectedValuesRef = useRef<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (!customOption.defaultValue) return;

    const defaultValues = Array.isArray(customOption.defaultValue)
      ? customOption.defaultValue.map(String)
      : String(customOption.defaultValue);

    selectedValuesRef.current = values.reduce(
      (acc, value) => {
        acc[value.id] = defaultValues.includes(value.id);
        return acc;
      },
      {} as { [key: string]: boolean },
    );
  }, [values, customOption.defaultValue]);

  // Handle checkbox change for multiple values
  const handleCheckboxChange = (valueId: string, checked: boolean) => {
    if (!onChange) return;

    if (checked) {
      selectedValuesRef.current[valueId] = true;
      onChange(Object.keys(selectedValuesRef.current));
    } else {
      delete selectedValuesRef.current[valueId];
      onChange(Object.keys(selectedValuesRef.current));
    }
  };

  // Handle single checkbox (boolean) change
  const handleSingleCheckboxChange = (checked: boolean) => {
    if (onChange) {
      onChange(checked);
    }
  };

  // If there are specific values, render checkboxes for each
  if (values.length > 0) {
    return (
      <div className="flex w-full flex-col gap-3">
        <LabelInput htmlFor={customOption.id} className="w-fit">
          <CustomOptionLabel customOption={customOption} />
        </LabelInput>
        {values.map((value: CustomOptionValue) => (
          <CheckboxInput
            key={value.id}
            name={`${customOption.id}[${value.id}]`}
            defaultChecked={selectedValuesRef.current[value.id]}
            label={<OptionValueRow value={value} />}
            onChange={(checked) => handleCheckboxChange(value.id, checked)}
            classes={{
              wrapper: "w-full",
            }}
          />
        ))}
      </div>
    );
  }

  // Single checkbox (boolean)
  return (
    <CheckboxInput
      name={customOption.id}
      label={customOption.name}
      onChange={handleSingleCheckboxChange}
      classes={{
        wrapper: "w-full",
      }}
    />
  );
}
